import { Tooltip } from "antd";
import classNames from "classnames";
// eslint-disable-next-line no-restricted-imports
import { useRouter } from "next/router";
import { FunctionComponent, ReactElement, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import strings from "rivals/components/pages/User/BillingInformation/strings";
import RivalsButton from "rivals/components/shared/Button";
import CustomButton from "rivals/components/shared/CustomButton";
import AddressAutoComplete from "rivals/components/shared/Form/Address";
import { BillingFieldsPartial } from "rivals/components/shared/Form/Address/types";
import Checkbox from "rivals/components/shared/Form/Checkbox";
import DebitAgreement from "rivals/components/shared/Form/DebitAgreement";
import Select from "rivals/components/shared/Form/Select";
import FormInput from "rivals/components/shared/Form/TextInput";
import { InputType } from "rivals/components/shared/Form/TextInput/types";
import Modal from "rivals/components/shared/Modal";
import PayPalCheckout from "rivals/components/shared/PayPalCheckout";
import { PayPalAuthorizeAction } from "rivals/components/shared/PayPalCheckout/types";
import Spinner from "rivals/components/shared/Spinner";
import SubscriptionAgreement from "rivals/components/shared/Subscription/SubscriptionAgreement";
import Error from "rivals/public/static/icons/career_highlights/decommit.svg";
import Signed from "rivals/public/static/icons/career_highlights/signed_loi.svg";
import { getHostUrl } from "rivals/redux/application/selectors";
import { isPayPalLoading } from "rivals/redux/user/selectors";
import { AddressFormKey, FORM_TEXT } from "rivals/services/billing/types";
import {
  CANCEL,
  CARD_TYPES,
  PAYPAL_ENVS,
  PAYPAL_PREPARE_BILLING_AGREEMENT_ACTION
} from "rivals/shared/constants";
import { PayPalAuthorizationData } from "rivals/shared/interfaces/User";
import { setData } from "rivals/shared/utils/qa";
import styles from "./BillingInformation.module.scss";
import { OwnProps, StateProps } from "./types";

const expDateMaxLength = 5;
const expDateSlice = 2;
const TOOLTIP_OFFSET_PIXELS = 10;

type Props = StateProps & OwnProps;

const BillingInformation: FunctionComponent<Props> = ({
  addressContainerRef,
  billingFormState,
  closeForm,
  isCheckingOut,
  ccField,
  cvvField,
  onBlur,
  onChange,
  onStateChange,
  railsConstants,
  submit,
  submitting,
  updateAddress,
  isDebit,
  setIsDebit,
  debitDisabled,
  debitAgreedState,
  getPayPalBillingAgreement,
  isAutoRenewAgreed,
  isPayPalEnabled = true,
  payPalEmail,
  payPalError,
  payPalToken,
  payPalTitle,
  payPalBillingAgreement,
  payPalAddress,
  requiresAutoRenewalOptIn,
  setIsAutoRenewAgreed,
  updatePayPalAuthorizationData,
  updatePayPalAddress,
  updatePayPalEmail,
  updatePayPalError,
  userEmail = "",
  valid
}) => {
  if (!railsConstants) {
    return null;
  }
  const [showChangePaymentModal, setShowChangePaymentModal] = useState<boolean>(
    false
  );

  const { asPath, basePath } = useRouter();
  const hostUrl = useSelector(getHostUrl);
  const isLoadingPayPal = useSelector(isPayPalLoading);
  const currentUrl = hostUrl + basePath + asPath;
  const showPayPal = PAYPAL_ENVS.includes(
    process.env.NEXT_PUBLIC_PAYPAL_ENV || ""
  );

  useEffect(() => {
    if (isPayPalEnabled && showPayPal) {
      getPayPalBillingAgreement({
        billingAgreementAction: PAYPAL_PREPARE_BILLING_AGREEMENT_ACTION,
        cancelUrl: currentUrl,
        returnUrl: currentUrl,
        userEmail
      });
    }
  }, [isPayPalEnabled]);

  // TODO: https://griosf.atlassian.net/browse/RVLS-4077
  const { debitTerms, smartyStreetsWebToken, states } = railsConstants;

  const {
    changePaymentMethod,
    creditCard,
    cvvHelp,
    cvvHelpAmex,
    expressCheckout,
    newPaymentAgreement,
    payPalAccount,
    payPalGenericError,
    unlinkPayment,
    update,
    updateBillingTerms,
    updatePaymentMethod,
    usingDebit
  } = strings;

  // Used when someone selects an address from SmartyStreets
  function addressSelect(autoCompleteResults: BillingFieldsPartial): void {
    updateAddress(autoCompleteResults);
  }

  function addressFormInput(
    formKey: AddressFormKey,
    label: string,
    autoComplete = false
  ): ReactElement {
    const groupClasses = classNames(
      styles[`${formKey}Group`],
      styles.billingFormInputGroup
    );

    const Input =
      autoComplete && smartyStreetsWebToken ? AddressAutoComplete : FormInput;

    let maxLength;

    if (formKey === "expirationDate") {
      maxLength = expDateMaxLength;
      const value = billingFormState.expirationDate.value;
      if (value.length > expDateSlice && !value.includes("/")) {
        billingFormState.expirationDate.value = [
          value.slice(0, expDateSlice),
          "/",
          value.slice(expDateSlice)
        ].join("");
      }
    }

    return (
      <Input
        className={groupClasses}
        data={`edit-billing-info-${formKey}`}
        feedback={billingFormState[formKey].error}
        htmlFor={formKey}
        label={label}
        maxLength={maxLength}
        onBlur={onBlur}
        onChange={onChange}
        onSelect={addressSelect}
        required={false}
        token={smartyStreetsWebToken}
        type={InputType.TEXT}
        value={billingFormState[formKey].value}
      />
    );
  }

  /**
   * Classnames for these two inputs are critical
   * OBI will not be able to attach the obick iframe
   * to the inputs if they are renamed
   */
  const ccClasses = classNames(
    { [styles.feedback]: billingFormState.cardNumber.error },
    [styles.cardInput],
    "obick-card-number"
  );
  const cvvClasses = classNames(
    { [styles.feedback]: billingFormState.cvv.error },
    [styles.cardInput],
    "obick-cvv"
  );

  const toolTipText = (
    <span {...setData("edit-billing-info", "tooltip-text")}>
      {[cvvHelp, cvvHelpAmex].join(" ")}
    </span>
  );

  const submitSection = (
    <>
      <div
        className={styles.terms}
        {...setData("edit-billing-info", "general-terms")}
      >
        {updateBillingTerms}
      </div>
      {submitting ? <Spinner /> : null}
      <div className={styles.buttonGroup}>
        <RivalsButton
          backgroundColor="transparent"
          borderColor="transparent"
          buttonText={CANCEL}
          className={styles.billingCancel}
          data="edit-billing-info-cancel-update"
          disabled={submitting}
          inverted
          onClick={closeForm}
        />
        <RivalsButton
          buttonText={update}
          className={styles.billingSubmit}
          data="edit-billing-info-update-payment"
          disabled={!valid || submitting || isCheckingOut}
          inverted
          onClick={submit}
        />
      </div>
    </>
  );

  const handlePaymentChange = (): void => {
    setShowChangePaymentModal(true);
  };

  const handleChangePaymentCancel = (): void => {
    setShowChangePaymentModal(false);
  };

  const handleChangePaymentConfirm = (): void => {
    if (payPalToken) {
      updatePayPalAddress();
      updatePayPalAuthorizationData();
      updatePayPalEmail();
    }
    setShowChangePaymentModal(false);
  };

  const handlePayPalError = (): void => {
    updatePayPalError(payPalGenericError);
  };

  const handleApprovePayPalPayment = async (
    data: PayPalAuthorizationData,
    actions: PayPalAuthorizeAction
  ): Promise<void> => {
    try {
      const orderDetails = await actions.order.get();
      const { payer } = orderDetails;
      const { address, name, email_address: email } = payer;
      const {
        address_line_1: street1,
        address_line_2: street2,
        admin_area_1: state,
        admin_area_2: city,
        postal_code: zip
      } = address;
      const { given_name: firstName, surname: lastName } = name;
      updatePayPalEmail(email);
      updatePayPalAuthorizationData(data);
      updatePayPalAddress({
        city,
        firstName,
        lastName,
        state,
        street1,
        street2,
        zip
      });
      updatePayPalError();
    } catch (error) {
      updatePayPalError(payPalGenericError);
      updatePayPalAuthorizationData();
      updatePayPalAddress();
    }
  };

  const isPayPalAuthorized = !!payPalToken && !!payPalAddress;

  const payPalSection = (
    <div className={styles.payPalSection}>
      <fieldset className={styles.paymentSection}>
        <legend className={styles.paymentTitle}>
          {payPalTitle || expressCheckout}
        </legend>
        <div className={styles.payPalContent}>
          <div className={styles.payPalCheckout}>
            <PayPalCheckout
              disabled={!isPayPalEnabled}
              isLoading={isLoadingPayPal}
              onApprovePayment={handleApprovePayPalPayment}
              onError={handlePayPalError}
              transactionId={payPalBillingAgreement?.transactionId}
            />
          </div>
          {isPayPalAuthorized && (
            <div className={styles.accountSection}>
              <div>{payPalAccount}: </div>
              <div className={styles.emailContainer}>
                <span className={styles.email}>{payPalEmail}</span>
                <img alt="authorized" src={payPalError ? Error : Signed} />
              </div>
            </div>
          )}
          {payPalError && (
            <div className={styles.payPalError}>{payPalError}</div>
          )}
        </div>
      </fieldset>
    </div>
  );

  return (
    <div className={styles.billingForm}>
      {showPayPal && payPalSection}
      <fieldset className={styles.paymentSection}>
        <legend className={styles.paymentTitle}>{creditCard}</legend>
        {payPalToken && (
          <div className={styles.changePayment}>
            <CustomButton
              buttonText={changePaymentMethod}
              onClick={handlePaymentChange}
            />
          </div>
        )}
        <div className={styles.creditCards}>
          {payPalToken && <div className={styles.creditCardOverlay} />}
          {CARD_TYPES.map((card, idx) => (
            <img
              alt={`${card} icon`}
              key={idx}
              src={`/static/credit_cards/${card}.png`}
              {...setData("edit-billing-info", `${card}-card-image`)}
            />
          ))}
        </div>
        {!payPalToken && (
          <>
            <div className={styles.cardSection}>
              <div className={styles.obickCardNumberGroup}>
                <label
                  className={styles.label}
                  htmlFor="obick-id-card-number"
                  {...setData("edit-billing-info", "card-number-label")}
                >
                  Card Number
                </label>
                <div
                  className={ccClasses}
                  ref={ccField}
                  {...setData("edit-billing-info", "card-number-input")}
                />
                <div className={styles.inputFeedback}>
                  {billingFormState.cardNumber.error}
                </div>
              </div>
              {addressFormInput("expirationDate", FORM_TEXT.EXPIRATION)}
              <div className={styles.obickCVVGroup}>
                <label
                  className={styles.label}
                  htmlFor="obick-id-cvv"
                  {...setData("edit-billing-info", "cvv-header")}
                >
                  CVV
                </label>
                <Tooltip
                  align={{ offset: [TOOLTIP_OFFSET_PIXELS, 0] }}
                  placement="topRight"
                  title={toolTipText}
                >
                  <span
                    className={styles.help}
                    {...setData("edit-billing-info", "help-icon")}
                  />
                </Tooltip>
                <div
                  className={cvvClasses}
                  ref={cvvField}
                  {...setData("edit-billing-info", "cvv-input")}
                />
                <div className={styles.inputFeedback}>
                  {billingFormState.cvv.error}
                </div>
              </div>
            </div>
            <Checkbox
              className={styles.isDebit}
              data="edit-billing-info-is-debit"
              disabled={debitDisabled}
              isChecked={isDebit}
              label={usingDebit}
              labelClassName={styles.optInLabel}
              onChange={(): void => {
                setIsDebit(!isDebit);
              }}
            />
            <div className={styles.nameGroup}>
              {addressFormInput("firstName", FORM_TEXT.FIRST_NAME)}
              {addressFormInput("lastName", FORM_TEXT.LAST_NAME)}
            </div>
            <div ref={addressContainerRef}></div>
            {addressFormInput("street1", FORM_TEXT.STREET_1, true)}
            {addressFormInput("street2", FORM_TEXT.STREET_2)}
            <div className={styles.cityStateZipSection}>
              {addressFormInput("city", FORM_TEXT.CITY)}
              <div className={styles.stateGroup}>
                <label
                  className={styles.label}
                  {...setData("edit-billing-info", "state-label")}
                >
                  {FORM_TEXT.STATE}
                </label>
                <Select<string>
                  className={styles.stateSelect}
                  data="edit-billing-info-state"
                  onChange={onStateChange}
                  options={states.map(state => ({
                    name: state.name,
                    value: state.code
                  }))}
                  value={billingFormState.state.value || states[0].code}
                />
              </div>
              {addressFormInput("zip", FORM_TEXT.ZIP)}
            </div>
            {isDebit && debitAgreedState && (
              <DebitAgreement
                debitAgreedState={debitAgreedState}
                debitTerms={debitTerms}
              />
            )}
          </>
        )}
      </fieldset>
      {submit && requiresAutoRenewalOptIn && (
        <SubscriptionAgreement
          isAutoRenewAgreed={isAutoRenewAgreed}
          onAutoRenewChange={(): void => {
            setIsAutoRenewAgreed(!isAutoRenewAgreed);
          }}
          title={newPaymentAgreement}
        />
      )}
      {submit && submitSection}
      <Modal
        handleCancel={handleChangePaymentCancel}
        handleConfirm={handleChangePaymentConfirm}
        open={showChangePaymentModal}
        title={updatePaymentMethod}
      >
        {unlinkPayment}
      </Modal>
    </div>
  );
};

export default BillingInformation;
